/**
 * How an entity's dates and times are drawn: the pattern they are on, the clock they use, and the zone
 * they are read in.
 *
 * <p>All of it comes from the platform, resolved through the entity's own choice, then its organization's,
 * then the platform's own default — see `LegalEntityResponse`. Nothing here is guessed: a client inventing
 * a format is how one row comes to read differently in two places.
 *
 * @property datePattern e.g. `DD-MM-YYYY`, `MM/DD/YYYY`, `YYYY-MM-DD`
 * @property timePattern e.g. `hh:mm AM` (twelve hour), `HH:mm` or `HH:mm:ss` (twenty-four)
 * @property zone        the IANA zone to display in, e.g. `Asia/Kolkata`
 * @property storedIn    the zone the platform WROTE the timestamp in. Its timestamps carry no offset, so
 *                       converting one is undefined without this, and assuming UTC against a server on
 *                       local time is wrong by the offset and silently so.
 */
export interface DisplayFormat {
  datePattern?: string | null;
  timePattern?: string | null;
  zone?: string | null;
  storedIn?: string | null;
}

/** The wall-clock fields of a stored timestamp, as written. */
interface Wall {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  hasTime: boolean;
}

const STORED = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Parses what the platform writes, and nothing else. Null for anything that is not one of its dates. */
function parseStored(value: string): Wall | null {
  const match = STORED.exec(value.trim());
  if (!match) return null;
  const [, year, month, day, hours, minutes, seconds] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hours: Number(hours ?? '0'),
    minutes: Number(minutes ?? '0'),
    seconds: Number(seconds ?? '0'),
    hasTime: hours !== undefined,
  };
}

/** A zone's offset from UTC in milliseconds at a given instant, daylight saving included. */
function offsetOf(zone: string, instant: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));

  const field = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? '0');
  /* Some locales spell midnight `24`; the modulo keeps it a valid hour and the date rolls over correctly. */
  const asUtc = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour') % 24,
    field('minute'),
    field('second'),
  );
  return asUtc - instant;
}

/**
 * The same instant, read in another zone.
 *
 * <p>The stored value is a wall-clock reading in `storedIn`, so it is turned into an instant using that
 * zone's offset at that moment, then read back in the target zone. Both offsets are asked of `Intl` rather
 * than assumed, so daylight saving comes from the platform's own tables instead of from arithmetic that is
 * wrong for half the year.
 *
 * <p>Either zone missing, the two equal, or a value with no time at all means there is nothing to convert:
 * the fields are returned as written, which is the honest answer rather than a shift in an unknown
 * direction.
 */
function inZone(wall: Wall, storedIn: string | null | undefined, zone: string | null | undefined): Wall {
  if (!storedIn || !zone || storedIn === zone || !wall.hasTime) return wall;

  try {
    /* The instant these fields would name if they were UTC, and the correction that makes them real. */
    const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hours, wall.minutes, wall.seconds);
    const instant = asUtc - offsetOf(storedIn, asUtc);
    const shown = new Date(instant + offsetOf(zone, instant));
    return {
      year: shown.getUTCFullYear(),
      month: shown.getUTCMonth() + 1,
      day: shown.getUTCDate(),
      hours: shown.getUTCHours(),
      minutes: shown.getUTCMinutes(),
      seconds: shown.getUTCSeconds(),
      hasTime: true,
    };
  } catch {
    /* An identifier this browser does not know: show the time as stored rather than as a guess. */
    return wall;
  }
}

const pad = (value: number): string => String(value).padStart(2, '0');

/** The configured pattern, or the platform's own spelling where nothing is configured. */
function chosen(pattern: string | null | undefined, fallback: string): string {
  const trimmed = pattern?.trim() ?? '';
  return trimmed === '' ? fallback : trimmed;
}

/**
 * The date part, drawn in the configured pattern.
 *
 * <p>Tokens are substituted rather than the pattern being matched against a list of known ones, so a
 * pattern somebody adds to the Date Format master works without a code change. `MMMM` and `MMM` are
 * supported for the month's name: the seeded patterns do not use them, so a customer wanting
 * "September 25, 2026" needs the data changed and not this file.
 */
function drawDate(wall: Wall, pattern: string): string {
  return pattern
    .replace(/YYYY/g, String(wall.year))
    /* The two-digit-year patterns (DD-MM-YY, YY-MM-DD, ...). After YYYY, so a four-digit year is not halved. */
    .replace(/YY/g, pad(wall.year % 100))
    .replace(/MMMM/g, MONTHS[wall.month - 1] ?? '')
    .replace(/MMM/g, (MONTHS[wall.month - 1] ?? '').slice(0, 3))
    .replace(/MM/g, pad(wall.month))
    .replace(/DD/g, pad(wall.day));
}

/**
 * The time part: twelve hour where the pattern says so, twenty-four where it does not.
 *
 * <p>The pattern decides, because that is exactly what the Time Format master stores — `hh:mm AM` against
 * "12 Hour", `HH:mm:ss` against "24 Hour", `HH:mm` against "24 Hour Without Seconds". Seconds are drawn
 * only when the pattern asks for them, which is why 24-hour reads `23:25` rather than `23:25:00`.
 */
function drawTime(wall: Wall, pattern: string): string {
  const twelveHour = pattern.includes('hh') || pattern.toUpperCase().includes('AM');
  const hour = twelveHour ? (wall.hours % 12 === 0 ? 12 : wall.hours % 12) : wall.hours;
  const meridiem = wall.hours < 12 ? 'AM' : 'PM';

  const drawn = pattern
    .replace(/hh/g, pad(hour))
    .replace(/HH/g, pad(wall.hours))
    .replace(/mm/g, pad(wall.minutes))
    .replace(/ss/g, pad(wall.seconds))
    /* `A` is the meridiem token the master now stores (`hh:mm A`); `AM` is the older literal spelling. */
    .replace(/AM\/PM|A\.?M\.?|\bA\b/gi, meridiem);

  /* A twelve-hour clock reads 9:05, not 09:05. Only the hour's own leading zero goes. */
  return twelveHour ? drawn.replace(/^0/, '') : drawn;
}

/**
 * A stored timestamp as a person reads it, in the format the entity is configured for.
 *
 * <h2>Why a format is passed in</h2>
 *
 * <p>The masters used to render whatever the response carried, and what it carries is
 * `2026-09-25T10:03:25.24248` — a machine's spelling. Drawing every date the same way was better and still
 * wrong: the platform lets an organization and an entity CHOOSE a date format, a time format and a zone,
 * and a screen that ignores the choice is a control that does nothing.
 *
 * <p>With no format given, or none configured, it falls back to the platform's own spelling of the
 * value — readable, and visibly not a choice anybody made.
 *
 * <h2>Read as written, then converted deliberately</h2>
 *
 * <p>These are `LocalDateTime`s: wall-clock readings with no zone. They are parsed as written and
 * converted only when the format says which zone they were written in and which to show them in.
 * `new Date(value)` is never used on one: it makes the browser guess a zone, so the same row reads
 * differently in two offices for no reason anybody chose.
 *
 * <h2>A date with no time stays a date</h2>
 *
 * <p>Financial year start and end are `LocalDate`. Appending midnight to a date somebody typed would
 * invent a precision the value does not have, and converting it between zones would move it a day.
 *
 * @returns the readable form, or `null` when the value is not a date the platform writes — the caller's
 *          signal to render it unchanged rather than to guess
 */
export function formatStoredDateTime(value: string, format: DisplayFormat = {}): string | null {
  const parsed = parseStored(value);
  if (!parsed) return null;

  const wall = inZone(parsed, format.storedIn, format.zone);
  const date = drawDate(wall, chosen(format.datePattern, 'YYYY-MM-DD'));
  if (!wall.hasTime) return date;

  return `${date}, ${drawTime(wall, chosen(format.timePattern, 'hh:mm AM'))}`;
}

/**
 * The calendar day a stored timestamp falls on, in the zone it is READ in: `2026-10-29`.
 *
 * <h2>Why the day is separate from the drawing of it</h2>
 *
 * <p>The notification panel groups rows under Today / Yesterday / Earlier, and that question is not the
 * same as "how does this timestamp read". The answer depends only on the zone, never on the entity's date
 * pattern -- a row written at 11pm in Mumbai is today for a reader in Mumbai whichever way they have
 * chosen to spell dates -- so the comparison is done on an ISO day here and the pattern is applied later,
 * by {@link formatStoredDateTime}, to the part a person actually sees.
 *
 * <p>Comparing the DRAWN strings instead would tie the grouping to the date pattern: `DD-MM-YYYY` and
 * `MM-DD-YYYY` sort differently, and a pattern with a month name does not sort at all.
 *
 * @returns the ISO day, or `null` for a value that is not one the platform writes
 */
export function storedCalendarDay(value: string, format: DisplayFormat = {}): string | null {
  const parsed = parseStored(value);
  if (!parsed) return null;
  const wall = inZone(parsed, format.storedIn, format.zone);
  return `${String(wall.year)}-${pad(wall.month)}-${pad(wall.day)}`;
}

/**
 * Today, in the zone the entity reads timestamps in -- the other half of the comparison.
 *
 * <p>Taken from `Intl` rather than from the browser's own date, because "today" for somebody reading an
 * Asia/Kolkata entity from a laptop in London is the entity's today. Between 18:30 and midnight UTC those
 * two differ, which is exactly when a notification would be filed under the wrong heading.
 *
 * @param zone the IANA zone, or null/unknown to use the browser's own day
 * @param now  the instant to read, injectable so a test can pin one
 */
export function calendarDayNow(zone?: string | null, now: Date = new Date()): string {
  if (zone) {
    try {
      /* en-CA spells a date YYYY-MM-DD, which is the form being compared. */
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now);
    } catch {
      /* An identifier this browser does not know: fall through to its own day rather than throw. */
    }
  }
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The day before an ISO day, so "yesterday" needs no date arithmetic at the call site. */
export function previousCalendarDay(day: string): string {
  const parts = day.split('-');
  const [year, month, date] = [Number(parts[0]), Number(parts[1]), Number(parts[2])];
  /* UTC, so the subtraction cannot land on a daylight-saving boundary and lose or repeat a day. */
  const stepped = new Date(Date.UTC(year, month - 1, date - 1));
  return `${String(stepped.getUTCFullYear())}-${pad(stepped.getUTCMonth() + 1)}-${pad(stepped.getUTCDate())}`;
}
